import React from 'react';
import { EmpPropriedadeIntelectualTab } from './EmpPropriedadeIntelectualTab';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { 
  BookOpen, Download, FileText, Shield, Users, Building2, HardHat,
  Workflow, BarChart3, ClipboardCheck, Receipt, AlertTriangle, Settings,
  Link2, Eye, CheckCircle2, Calculator, Lock, Award, Layers, Play, Scale
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export const EmpManualView: React.FC = () => {
  const { isAdmin, isCoAdmin } = useAuth();
  const isGlobalAdmin = isAdmin || isCoAdmin;

  const handleDownloadPDF = () => {
    window.open('/docs/SIAFO_Manual_Empreendimentos_v1.0.html', '_blank');
  };
  const handleDownloadMD = () => {
    window.open('/docs/SIAFO_Manual_Empreendimentos_v1.0.md', '_blank');
  };
  const handleDownloadEVMPDF = () => {
    window.open('/docs/SIAFO_Manual_Medicao_EVM_v2.0.html', '_blank');
  };
  const handleDownloadEVMMD = () => {
    window.open('/docs/SIAFO_Manual_Medicao_EVM_v2.0.md', '_blank');
  };

  const chapters = [
    { num: '1', title: 'Visão Geral do Módulo', icon: BookOpen, desc: 'Finalidade, submódulos e contexto ativo' },
    { num: '2', title: 'Papéis e Responsabilidades', icon: Users, desc: 'Admin, Gestor, Fiscal, Executor, Diretoria' },
    { num: '3', title: 'Fluxo Operacional Completo', icon: Workflow, desc: '13 etapas — do empreendimento ao encerramento' },
    { num: '4', title: 'Correlação Orçamento × Cronograma', icon: Link2, desc: 'Modelo de dados, vínculo, PV e exemplo prático' },
    { num: '5', title: 'Detalhamento por Módulo', icon: ClipboardCheck, desc: '12 módulos com dependências e boas práticas' },
    { num: '6', title: 'Fluxos Alternativos', icon: AlertTriangle, desc: 'Rejeições, glosas, atrasos, estouros, aditivos' },
    { num: '7', title: 'Boas Práticas', icon: Shield, desc: 'Calendário, checklist e erros comuns' },
    { num: '8', title: 'Visão Gerencial e Executiva', icon: Eye, desc: 'Rotina do gestor, leitura pela diretoria' },
    { num: '9', title: 'Encadeamento Lógico', icon: BarChart3, desc: 'Ciclo completo e calendário mensal' },
    { num: '10', title: 'Glossário', icon: FileText, desc: 'EVM, SPI, CPI, BAC, Glosa e mais' },
  ];

  const evmChapters = [
    { num: '1', title: 'Visão Geral do Modelo', icon: BookOpen, desc: 'Cadeia lógica, princípios e EAP por convenção' },
    { num: '2', title: 'Tipos de Contrato', icon: Building2, desc: 'PU, EPC e Híbrido — tabela comparativa e riscos' },
    { num: '3', title: 'Cadastro — Tipo de Medição', icon: Settings, desc: 'Campo tipo_medicao, impacto e imutabilidade' },
    { num: '4', title: 'Planilha Orçamentária', icon: Receipt, desc: 'Como estruturar por tipo de contrato' },
    { num: '5', title: 'Cronograma e Métodos', icon: Layers, desc: 'metodo_medicao, valor_ponderado e validações' },
    { num: '6', title: 'Execução e Medição', icon: Play, desc: 'Tela por método: quantitativo, marco, percentual' },
    { num: '7', title: 'Cálculo EVM (Detalhado)', icon: Calculator, desc: 'PV, EV, AC, SPI, CPI — 3 exemplos numéricos' },
    { num: '8', title: 'Governança e Validações', icon: Shield, desc: 'Motor X1–X6, severidades e prevenção' },
    { num: '9', title: 'Retrocompatibilidade', icon: Lock, desc: 'Defaults, histórico e congelamento' },
    { num: '10', title: 'Confiança Executiva', icon: Award, desc: 'Decisão estratégica, auditoria e TCPI' },
  ];

  const steps = [
    { icon: Building2, label: 'Empreendimento' },
    { icon: FileText, label: 'Contrato' },
    { icon: Settings, label: 'Configuração' },
    { icon: Receipt, label: 'Planilha' },
    { icon: Workflow, label: 'Cronograma' },
    { icon: HardHat, label: 'Execução' },
    { icon: CheckCircle2, label: 'Validação' },
    { icon: Receipt, label: 'Medição' },
    { icon: BarChart3, label: 'Indicadores' },
    { icon: AlertTriangle, label: 'Alertas' },
    { icon: ClipboardCheck, label: 'Relatórios' },
    { icon: Building2, label: 'Encerramento' },
  ];

  const handleDownloadManualCompleto = () => {
    window.open('/docs/NetUno_Manual_Completo_v3.0.html', '_blank');
  };
  const handleDownloadDossie = () => {
    window.open('/docs/NetUno_Dossie_Homologacao_Investidores.html', '_blank');
  };

  return (
    <div className="space-y-6">
      <Card className="border-l-4 border-l-emp-primary">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emp-primary/10">
                <BookOpen className="w-6 h-6 text-emp-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Manuais Funcionais — Módulo Operacional</CardTitle>
                <CardDescription>Documentação técnica e executiva completa</CardDescription>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {isGlobalAdmin && (
                <Button onClick={handleDownloadDossie} size="sm" className="gap-2">
                  <Shield className="w-4 h-4" />
                  Dossiê Investidores
                </Button>
              )}
              <Button onClick={handleDownloadManualCompleto} size="sm" variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                Manual Completo v3.0
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Tabs defaultValue="evm">
        <TabsList className={`grid w-full ${isGlobalAdmin ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <TabsTrigger value="operacional" className="gap-2">
            <Workflow className="w-4 h-4" /> Operacional
          </TabsTrigger>
          <TabsTrigger value="evm" className="gap-2">
            <Calculator className="w-4 h-4" /> Medição e EVM
          </TabsTrigger>
          {isGlobalAdmin && (
            <TabsTrigger value="pi" className="gap-2">
              <Scale className="w-4 h-4" /> Propriedade Intelectual
            </TabsTrigger>
          )}
        </TabsList>

        {/* ─── Manual Operacional ─────────────────── */}
        <TabsContent value="operacional" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-lg">Manual Funcional — Operacional</CardTitle>
                  <CardDescription>Versão 2.0 · Fevereiro 2026 · Uso interno</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleDownloadPDF} size="sm" className="gap-2 bg-emp-primary hover:bg-emp-primary/90">
                    <Download className="w-4 h-4" /> PDF
                  </Button>
                  <Button onClick={handleDownloadMD} size="sm" variant="outline" className="gap-2">
                    <Download className="w-4 h-4" /> MD
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Guia completo para operação, gestão e governança de contratos de obras públicas 
                — do cadastramento inicial ao encerramento.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" /> Sumário (10 capítulos)
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-3">
              {chapters.map(ch => (
                <div key={ch.num} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-emp-primary/10 flex items-center justify-center text-sm font-bold text-emp-primary flex-shrink-0">
                    {ch.num}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <ch.icon className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{ch.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{ch.desc}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Workflow className="w-5 h-5" /> Fluxo Operacional (12 etapas)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-2">
                {steps.map((s, i) => (
                  <React.Fragment key={i}>
                    <Badge variant="outline" className="gap-1.5 py-1.5 px-3">
                      <s.icon className="w-3.5 h-3.5" /> {s.label}
                    </Badge>
                    {i < steps.length - 1 && <span className="text-muted-foreground">→</span>}
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Manual Medição e EVM v2.0 ───────────── */}
        <TabsContent value="evm" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-lg">Manual Funcional — Medição e EVM</CardTitle>
                  <CardDescription>Versão 2.0 · Fevereiro 2026 · Uso interno e executivo</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleDownloadEVMPDF} size="sm" className="gap-2 bg-emp-primary hover:bg-emp-primary/90">
                    <Download className="w-4 h-4" /> PDF
                  </Button>
                  <Button onClick={handleDownloadEVMMD} size="sm" variant="outline" className="gap-2">
                    <Download className="w-4 h-4" /> MD
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Documentação completa e didática sobre modelos de medição (Preço Unitário, EPC e Híbrido), 
                estruturação de planilhas e cronogramas, cálculos EVM com exemplos numéricos, 
                governança de dados e visão executiva para diretoria e auditoria.
              </p>
              <div className="mt-3 p-2 rounded-lg bg-success/10 border border-success/30">
                <p className="text-xs text-success font-medium">
                  ✓ v2.0 — 10 capítulos + Cheat Sheet · Exemplos numéricos PU, EPC e Híbrido · Seção de confiança executiva com TCPI
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Sumário 10 capítulos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" /> Sumário (10 capítulos + Cheat Sheet)
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-3">
              {evmChapters.map(ch => (
                <div key={ch.num} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-emp-primary/10 flex items-center justify-center text-sm font-bold text-emp-primary flex-shrink-0">
                    {ch.num}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <ch.icon className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{ch.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{ch.desc}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Referência Rápida inline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="w-5 h-5" /> Referência Rápida — Cálculo EVM por Método
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg border bg-muted/20 space-y-2">
                  <Badge variant="secondary">Quantitativo</Badge>
                  <p className="text-xs text-muted-foreground mt-2">Preço Unitário / Híbrido</p>
                  <div className="text-sm space-y-1 mt-2">
                    <p><strong>PV</strong> = valor_item × fração temporal</p>
                    <p><strong>EV</strong> = (qty exec / qty contrat) × valor_item</p>
                  </div>
                  <p className="text-xs text-muted-foreground">EV ≤ valor do item (razão ≤ 1,0)</p>
                </div>
                <div className="p-4 rounded-lg border bg-muted/20 space-y-2">
                  <Badge variant="secondary">Marco</Badge>
                  <p className="text-xs text-muted-foreground mt-2">EPC / Híbrido</p>
                  <div className="text-sm space-y-1 mt-2">
                    <p><strong>PV</strong> = valor_ponderado (se data passou)</p>
                    <p><strong>EV</strong> = valor_ponderado (se 100%)</p>
                  </div>
                  <p className="text-xs text-muted-foreground">Binário: 0% ou 100%</p>
                </div>
                <div className="p-4 rounded-lg border bg-muted/20 space-y-2">
                  <Badge variant="secondary">Percentual</Badge>
                  <p className="text-xs text-muted-foreground mt-2">EPC / Híbrido</p>
                  <div className="text-sm space-y-1 mt-2">
                    <p><strong>PV</strong> = valor_ponderado × fração temporal</p>
                    <p><strong>EV</strong> = valor_ponderado × (% / 100)</p>
                  </div>
                  <p className="text-xs text-muted-foreground">Percentual ≤ 100%</p>
                </div>
              </div>

              <div className="p-4 rounded-lg border bg-muted/10">
                <p className="text-sm font-medium mb-2">Consolidação Final</p>
                <p className="text-sm text-muted-foreground">
                  EV total = Σ EV<sub>quantitativo</sub> + Σ EV<sub>marco</sub> + Σ EV<sub>percentual</sub>
                </p>
                <p className="text-sm text-muted-foreground">
                  SPI = EV / PV · CPI = EV / AC · EAC = BAC / CPI · TCPI = (BAC − EV) / (BAC − AC)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Tabela comparativa inline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Layers className="w-5 h-5" /> Comparação — Tipos de Contrato
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium">Aspecto</th>
                      <th className="text-left py-2 pr-4 font-medium">Preço Unitário</th>
                      <th className="text-left py-2 pr-4 font-medium">EPC</th>
                      <th className="text-left py-2 font-medium">Híbrido</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 pr-4 font-medium">Base de medição</td>
                      <td className="py-2 pr-4">Quantidade × preço</td>
                      <td className="py-2 pr-4">Marco / evento</td>
                      <td className="py-2">Por atividade</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4 font-medium">Método(s)</td>
                      <td className="py-2 pr-4">Quantitativo</td>
                      <td className="py-2 pr-4">Marco ou %</td>
                      <td className="py-2">Qualquer</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4 font-medium">Vínculo planilha</td>
                      <td className="py-2 pr-4">Obrigatório</td>
                      <td className="py-2 pr-4">Não</td>
                      <td className="py-2">Por atividade</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4 font-medium">Granularidade PV</td>
                      <td className="py-2 pr-4">Contínua (linear)</td>
                      <td className="py-2 pr-4">Discreta (saltos)</td>
                      <td className="py-2">Mista</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-medium">Exemplo</td>
                      <td className="py-2 pr-4">Terraplenagem</td>
                      <td className="py-2 pr-4">Subestação</td>
                      <td className="py-2">Obra + comissionamento</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Exemplo numérico inline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5" /> Exemplo — Contrato Híbrido (BAC = R$ 1.000.000)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium">Atividade</th>
                      <th className="text-left py-2 pr-4 font-medium">Método</th>
                      <th className="text-right py-2 pr-4 font-medium">Valor Ref.</th>
                      <th className="text-right py-2 pr-4 font-medium">PV</th>
                      <th className="text-right py-2 font-medium">EV</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 pr-4">Terraplenagem</td>
                      <td className="py-2 pr-4"><Badge variant="outline" className="text-xs">Quantitativo</Badge></td>
                      <td className="py-2 pr-4 text-right">R$ 300.000</td>
                      <td className="py-2 pr-4 text-right">R$ 300.000</td>
                      <td className="py-2 text-right">R$ 200.000</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">Subestação</td>
                      <td className="py-2 pr-4"><Badge variant="outline" className="text-xs">Marco</Badge></td>
                      <td className="py-2 pr-4 text-right">R$ 500.000</td>
                      <td className="py-2 pr-4 text-right">R$ 0</td>
                      <td className="py-2 text-right">R$ 0</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4">Montagem</td>
                      <td className="py-2 pr-4"><Badge variant="outline" className="text-xs">Percentual</Badge></td>
                      <td className="py-2 pr-4 text-right">R$ 200.000</td>
                      <td className="py-2 pr-4 text-right">R$ 62.222</td>
                      <td className="py-2 text-right">R$ 90.000</td>
                    </tr>
                    <tr className="font-medium">
                      <td className="py-2 pr-4" colSpan={3}>Total</td>
                      <td className="py-2 pr-4 text-right">R$ 362.222</td>
                      <td className="py-2 text-right">R$ 290.000</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                <Badge variant="secondary">SPI = 0,80</Badge>
                <Badge variant="secondary">CPI = 0,94</Badge>
                <Badge variant="secondary">EAC = R$ 1.063.830</Badge>
                <span className="self-center flex gap-2">
                  <Badge variant="warning" className="text-xs">Atraso de 20%</Badge>
                  <Badge variant="expired" className="text-xs">Leve estouro de custo</Badge>
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Decisão estratégica inline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5" /> Tomada de Decisão — Guia Executivo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-medium">Indicador</th>
                      <th className="text-left py-2 pr-4 font-medium">Leitura</th>
                      <th className="text-left py-2 font-medium">Ação Recomendada</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 pr-4"><code className="text-xs">SPI &lt; 0,85</code></td>
                      <td className="py-2 pr-4">Atraso significativo</td>
                      <td className="py-2">Reunião de reprogramação</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4"><code className="text-xs">CPI &lt; 0,90</code></td>
                      <td className="py-2 pr-4">Estouro de custo</td>
                      <td className="py-2">Auditoria de medições</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4"><code className="text-xs">SPI × CPI &lt; 0,75</code></td>
                      <td className="py-2 pr-4">Projeto em risco</td>
                      <td className="py-2">Escalar para diretoria</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4"><code className="text-xs">TCPI &gt; 1,20</code></td>
                      <td className="py-2 pr-4">Recuperação improvável</td>
                      <td className="py-2">Considerar revisão contratual</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Propriedade Intelectual ───────────── */}
        {isGlobalAdmin && (
          <TabsContent value="pi" className="mt-4">
            <EmpPropriedadeIntelectualTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};